import { ProviderCategory } from '@/api/model';
import type { VoucherProviderCreate } from '@/api/model/voucherProviderCreate';
import { useCreateProvider } from '@/api/voucher-providers/voucher-providers';
import React, { useEffect } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api-error';
import { VoucherProviderForm, type VoucherProviderFormData } from './VoucherProviderForm';

interface VoucherProviderCreateFormProps {
  onFormDataChange?: (data: VoucherProviderFormData) => void;
  hideLogoField?: boolean;
  onFormInstance?: (form: UseFormReturn<VoucherProviderFormData>) => void;
}

export const VoucherProviderCreateForm: React.FC<VoucherProviderCreateFormProps> = ({
  onFormDataChange,
  hideLogoField = false,
  onFormInstance,
}) => {
  const navigate = useNavigate();

  const form = useForm<VoucherProviderFormData>({
    defaultValues: {
      name: '',
      email: '',
      address: '',
      phone: '',
      short_description: '',
      logo_urls: [],
      category: ProviderCategory.SERVICES,
    },
  });

  const createProviderMutation = useCreateProvider();

  const watchedData = useWatch({ control: form.control });

  // Watch for form changes and update preview
  useEffect(() => {
    if (!onFormDataChange || !watchedData) return;
    onFormDataChange(watchedData as VoucherProviderFormData);
  }, [onFormDataChange, watchedData]);

  // Pass form instance to parent
  useEffect(() => {
    if (onFormInstance) {
      onFormInstance(form);
    }
  }, [form, onFormInstance]);

  const onSubmit = (data: VoucherProviderFormData) => {
    // Extract logo URL from uploaded images array
    const logoUrl = data.logo_urls && data.logo_urls.length > 0 ? data.logo_urls[0] : undefined;

    const providerData: VoucherProviderCreate = {
      name: data.name,
      email: data.email,
      address: data.address,
      phone: data.phone || undefined,
      short_description: data.short_description || undefined,
      logo_url: logoUrl,
      category: data.category,
    };

    createProviderMutation
      .mutateAsync({ data: providerData })
      .then(() => {
        toast.success('Gutscheinpartner erfolgreich erstellt');
        navigate('/admin/providers');
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error, 'Erstellen des Gutscheinpartners fehlgeschlagen'));
      });
  };

  const handleCancel = () => {
    navigate('/admin/providers');
  };

  return (
    <VoucherProviderForm
      form={form}
      onSubmit={onSubmit}
      isSubmitting={createProviderMutation.isPending}
      submitText='Partner erstellen'
      onCancel={handleCancel}
      cancelText='Abbrechen'
      hideLogoField={hideLogoField}
    />
  );
};
